import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { User, Mail, Key, CreditCard, LogOut, Save, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { CreditPurchase } from '../components/payments/CreditPurchase';
import { getCreditBalance } from '../lib/api';
export function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  // User data
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Credits
  const [credits, setCredits] = useState(0);
  const [loadingCredits, setLoadingCredits] = useState(true);
  useEffect(() => {
    loadUserData();
    loadCredits();
  }, []);
  const loadUserData = async () => {
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (user) {
        setEmail(user.email || '');
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };
  const loadCredits = async () => {
    setLoadingCredits(true);
    try {
      const balance = await getCreditBalance();
      setCredits(balance);
    } catch (error) {
      console.error('Error loading credits:', error);
      // If endpoint doesn't exist yet, default to 0
      setCredits(0);
    } finally {
      setLoadingCredits(false);
    }
  };
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    if (newPassword !== confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Passwords do not match'
      });
      setLoading(false);
      return;
    }
    if (newPassword.length < 6) {
      setMessage({
        type: 'error',
        text: 'Password must be at least 6 characters'
      });
      setLoading(false);
      return;
    }
    try {
      const {
        error
      } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      setMessage({
        type: 'success',
        text: 'Password updated successfully'
      });
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to update password'
      });
    } finally {
      setLoading(false);
    }
  };
  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  return <div className="container py-8 max-w-4xl" data-id="element-1913">
      <div className="mb-8" data-id="element-1914">
        <h1 className="text-3xl font-bold mb-2" data-id="element-1915">Settings</h1>
        <p className="text-muted-foreground" data-id="element-1916">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="space-y-6" data-id="element-1917">
        {/* Message */}
        {message && <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/20' : 'bg-destructive/10 border border-destructive/20'}`} data-id="element-1918">
            {message.type === 'success' ? <CheckCircle size={20} className="text-green-500" data-id="element-1919" /> : <AlertCircle size={20} className="text-destructive" data-id="element-1920" />}
            <p className={`text-sm ${message.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`} data-id="element-1921">
              {message.text}
            </p>
          </div>}

        {/* Account Information */}
        <section data-id="element-1922">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" data-id="element-1923">
            <User size={20} data-id="element-1924" />
            Account Information
          </h2>
          <Card data-id="element-1925">
            <CardContent className="p-6 space-y-4" data-id="element-1926">
              <div data-id="element-1927">
                <label className="block text-sm font-medium mb-2" data-id="element-1928">
                  Email Address
                </label>
                <div className="flex items-center gap-2" data-id="element-1929">
                  <Mail size={16} className="text-muted-foreground" data-id="element-1930" />
                  <Input type="email" value={email} disabled data-id="element-1931" />
                </div>
                <p className="text-xs text-muted-foreground mt-1" data-id="element-1932">
                  Contact support to change your email address
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Change Password */}
        <section data-id="element-1933">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" data-id="element-1934">
            <Key size={20} data-id="element-1935" />
            Change Password
          </h2>
          <Card data-id="element-1936">
            <CardContent className="p-6" data-id="element-1937">
              <form onSubmit={handleUpdatePassword} className="space-y-4" data-id="element-1938">
                <div data-id="element-1939">
                  <label className="block text-sm font-medium mb-2" data-id="element-1940">
                    New Password
                  </label>
                  <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password" required data-id="element-1941" />
                </div>

                <div data-id="element-1942">
                  <label className="block text-sm font-medium mb-2" data-id="element-1943">
                    Confirm New Password
                  </label>
                  <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" required data-id="element-1944" />
                </div>

                <Button type="submit" disabled={loading} data-id="element-1945">
                  {loading ? <>
                      <Loader2 size={16} className="mr-2 animate-spin" data-id="element-1946" />
                      Updating...
                    </> : <>
                      <Save size={16} className="mr-2" data-id="element-1947" />
                      Update Password
                    </>}
                </Button>
              </form>
            </CardContent>
          </Card>
        </section>

        {/* Credits & Billing */}
        <section data-id="element-1948">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" data-id="element-1949">
            <CreditCard size={20} data-id="element-1950" />
            Credits & Billing
          </h2>
          <Card data-id="element-1951">
            <CardContent className="p-6" data-id="element-1952">
              {loadingCredits ? <div className="flex items-center justify-center py-8" data-id="element-1953">
                  <Loader2 size={24} className="animate-spin text-primary" data-id="element-1954" />
                </div> : <CreditPurchase currentCredits={credits} onPurchaseComplete={loadCredits} data-id="element-1955" />}
            </CardContent>
          </Card>
        </section>

        {/* Sign Out */}
        <section data-id="element-1956">
          <Card className="border-destructive/20" data-id="element-1957">
            <CardContent className="p-6" data-id="element-1958">
              <h3 className="font-semibold mb-2" data-id="element-1959">Sign Out</h3>
              <p className="text-sm text-muted-foreground mb-4" data-id="element-1960">
                Sign out of your account on this device
              </p>
              <Button variant="destructive" onClick={handleSignOut} data-id="element-1961">
                <LogOut size={16} className="mr-2" data-id="element-1962" />
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>;
}